import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ProjectService } from 'src/app/Services/project.service';
import { DummyDataService } from 'src/app/Services/dummy-data.service';

export interface AbsenceCalendarDialogData {
  startDate: string;
  endDate: string;
  teamId: number | null;
  shiftId: number | null;
  rangeLabel: string;
  teamList?: any[];
  shiftList?: any[];
  employeeList?: any[];
}

export interface AbsenceCalendarDialogResult {
  openUserWise?: boolean;
  user_id?: number;
  name?: string;
}

type AbsenceFocusFilter = 'all' | 'absent' | 'present' | 'ot';

@Component({
  selector: 'app-absence-calendar-dialog',
  templateUrl: './absence-calendar-dialog.component.html',
  styleUrl: './absence-calendar-dialog.component.css',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, NgxSkeletonLoaderModule],
  providers: [DatePipe],
})
export class AbsenceCalendarDialogComponent implements OnInit {
  readonly hint =
    'Absent = zero logs on a scheduled working day. Logs outside the shift (weekend / off-day) show as Off-shift / overtime — they are never hidden.';

  loading = true;
  absenceDays: any[] = [];
  absenceSummary: any = null;
  selectedAbsenceDetail: any = null;
  absenceDayExpanded: Record<string, boolean> = {};
  readonly absencePreviewLimit = 4;

  filterTeamId: number | null = null;
  filterShiftId: number | null = null;
  filterEmployeeId: number | null = null;
  filterFocus: AbsenceFocusFilter = 'all';

  teamList: any[] = [];
  shiftList: any[] = [];
  employeeOptions: { id: number; name: string }[] = [];

  private rawDays: any[] = [];
  private rawSummary: any = null;
  private rawEmployees: any[] = [];

  constructor(
    private dialogRef: MatDialogRef<AbsenceCalendarDialogComponent, AbsenceCalendarDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: AbsenceCalendarDialogData,
    private projectService: ProjectService,
    private _dummyDataService: DummyDataService,
    public datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.filterTeamId = this.data.teamId != null ? Number(this.data.teamId) : null;
    this.filterShiftId = this.data.shiftId != null ? Number(this.data.shiftId) : null;
    this.teamList = Array.isArray(this.data.teamList) ? this.data.teamList : [];
    this.shiftList = Array.isArray(this.data.shiftList) ? this.data.shiftList : [];
    this.employeeOptions = this.normalizeEmployeeOptions(this.data.employeeList);
    this.load();
  }

  private normalizeEmployeeOptions(list: any[] | undefined): { id: number; name: string }[] {
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((e) => ({
        id: Number(e?.id ?? e?.user_id),
        name: String(e?.name || e?.display_name || '').trim() || `User #${e?.id ?? e?.user_id}`,
      }))
      .filter((e) => Number.isFinite(e.id) && e.id > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  teamLabel(team: any): string {
    return team?.title?.rendered || team?.name || `Team #${team?.id}`;
  }

  shiftLabel(shift: any): string {
    return shift?.shift_name || `Shift #${shift?.id}`;
  }

  onScopeFilterChange(): void {
    this.selectedAbsenceDetail = null;
    this.load();
  }

  onViewFilterChange(): void {
    this.applyViewFilters();
  }

  clearFilters(): void {
    this.filterTeamId = null;
    this.filterShiftId = null;
    this.filterEmployeeId = null;
    this.filterFocus = 'all';
    this.selectedAbsenceDetail = null;
    this.load();
  }

  async load(): Promise<void> {
    const startDate = this.data.startDate;
    const endDate = this.data.endDate;
    if (!startDate || !endDate) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.projectService
      .getTimesheetPeriodAbsenceCalendar(startDate, endDate, this.filterTeamId, this.filterShiftId)
      .subscribe({
        next: async (res: any) => {
          const checkDemo = await this._dummyDataService.checkIsAvailableDemoData<any>('AbsenceCalendar');
          if (checkDemo.status) {
            this.ingestPayload(checkDemo.data || {});
            return;
          }
          this.ingestPayload(res?.data || {});
        },
        error: async () => {
          const checkDemo = await this._dummyDataService.checkIsAvailableDemoData<any>('AbsenceCalendar');
          if (checkDemo.status) {
            this.ingestPayload(checkDemo.data || {});
            return;
          }
          this.ingestPayload({});
        },
      });
  }

  private ingestPayload(d: any): void {
    this.rawDays = Array.isArray(d?.days) ? d.days : [];
    this.rawSummary = d?.summary || null;
    this.rawEmployees = Array.isArray(d?.employees) ? d.employees : [];
    if (!this.employeeOptions.length && this.rawEmployees.length) {
      this.employeeOptions = this.normalizeEmployeeOptions(this.rawEmployees);
    }
    this.absenceDayExpanded = {};
    this.applyViewFilters();
    this.loading = false;
  }

  private applyViewFilters(): void {
    const empId = this.filterEmployeeId != null ? Number(this.filterEmployeeId) : null;
    const focus = this.filterFocus;

    const days = this.rawDays.map((day) => {
      let absent = Array.isArray(day?.absent_employees) ? [...day.absent_employees] : [];
      let present = Array.isArray(day?.present_employees) ? [...day.present_employees] : [];
      let off = Array.isArray(day?.off_schedule_employees) ? [...day.off_schedule_employees] : [];

      if (empId) {
        absent = absent.filter((e) => Number(e?.user_id) === empId);
        present = present.filter((e) => Number(e?.user_id) === empId);
        off = off.filter((e) => Number(e?.user_id) === empId);
      }

      const scheduled = empId
        ? absent.length + present.length > 0
          ? 1
          : 0
        : day?.scheduled_employees;

      return {
        ...day,
        absent_employees: absent,
        present_employees: present,
        off_schedule_employees: off,
        absent_count: absent.length,
        present_count: present.length,
        scheduled_employees: scheduled,
      };
    });

    this.absenceDays = days.filter((day) => {
      if (focus === 'absent') {
        return day.absent_count > 0;
      }
      if (focus === 'present') {
        return (day.present_employees || []).length > 0;
      }
      if (focus === 'ot') {
        return (day.off_schedule_employees || []).length > 0;
      }
      return true;
    });

    this.absenceSummary = this.buildSummaryFromDays(this.absenceDays, this.rawSummary);
  }

  private buildSummaryFromDays(days: any[], fallback: any): any {
    const withAbsence = new Set<number>();
    const withPresence = new Set<number>();
    const withOt = new Set<number>();
    let totalAbsent = 0;
    let totalPresent = 0;
    let totalOt = 0;
    for (const day of days) {
      for (const e of day.absent_employees || []) {
        const id = Number(e?.user_id);
        if (id) {
          withAbsence.add(id);
        }
        totalAbsent++;
      }
      for (const e of day.present_employees || []) {
        const id = Number(e?.user_id);
        if (id) {
          withPresence.add(id);
        }
        totalPresent++;
      }
      for (const e of day.off_schedule_employees || []) {
        const id = Number(e?.user_id);
        if (id) {
          withOt.add(id);
        }
        totalOt++;
      }
    }
    return {
      ...(fallback || {}),
      employees_with_absence: withAbsence.size,
      total_absent_days: totalAbsent,
      employees_with_presence: withPresence.size,
      total_present_days: totalPresent,
      employees_with_off_schedule: withOt.size,
      total_off_schedule_events: totalOt,
    };
  }

  close(): void {
    this.dialogRef.close();
  }

  selectAbsencePill(day: any, employee: any): void {
    this.selectedAbsenceDetail = {
      date: day?.date || '',
      ...employee,
    };
  }

  absenceEmployeesForDisplay(day: any): any[] {
    const list = Array.isArray(day?.absent_employees) ? day.absent_employees : [];
    const key = day?.date as string;
    if (!key || list.length <= this.absencePreviewLimit) {
      return list;
    }
    if (this.absenceDayExpanded[key]) {
      return list;
    }
    return list.slice(0, this.absencePreviewLimit);
  }

  absenceHiddenCount(day: any): number {
    const list = Array.isArray(day?.absent_employees) ? day.absent_employees : [];
    const n = list.length - this.absencePreviewLimit;
    return n > 0 ? n : 0;
  }

  absenceScheduledCount(day: any): number | null {
    const v = day?.scheduled_employees;
    if (v === undefined || v === null) {
      return null;
    }
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  isAbsenceAllPresentDay(day: any): boolean {
    if (day?.absent_count) {
      return false;
    }
    if (this.presentCount(day) > 0) {
      return false;
    }
    if (this.offScheduleCount(day) > 0) {
      return false;
    }
    const s = this.absenceScheduledCount(day);
    if (s === null) {
      return true;
    }
    return s > 0;
  }

  isAbsenceNoScheduledWorkDay(day: any): boolean {
    if (day?.absent_count) {
      return false;
    }
    if (this.presentCount(day) > 0) {
      return false;
    }
    if (this.offScheduleCount(day) > 0) {
      return false;
    }
    return this.absenceScheduledCount(day) === 0;
  }

  offScheduleCount(day: any): number {
    const list = day?.off_schedule_employees;
    return Array.isArray(list) ? list.length : 0;
  }

  presentCount(day: any): number {
    const list = day?.present_employees;
    if (Array.isArray(list)) {
      return list.length;
    }
    const n = Number(day?.present_count);
    return isNaN(n) ? 0 : n;
  }

  selectOffSchedulePill(day: any, row: any): void {
    this.selectedAbsenceDetail = {
      date: day?.date || '',
      user_id: row?.user_id,
      name: row?.name,
      shift_name: row?.shift_name || '—',
      shift_start_time: '—',
      shift_end_time: '—',
      reason: row?.note || 'Off-shift / overtime (activity on a non-working day)',
    };
  }

  isAbsenceDayExpanded(day: any): boolean {
    const key = day?.date as string;
    return !!(key && this.absenceDayExpanded[key]);
  }

  toggleAbsenceDayExpand(day: any, event?: Event): void {
    event?.stopPropagation();
    const key = day?.date as string;
    if (!key) {
      return;
    }
    this.absenceDayExpanded = {
      ...this.absenceDayExpanded,
      [key]: !this.absenceDayExpanded[key],
    };
  }

  openUserWiseFromDetail(): void {
    const userId = this.selectedAbsenceDetail?.user_id;
    if (!userId) {
      return;
    }
    this.dialogRef.close({
      openUserWise: true,
      user_id: userId,
      name: this.selectedAbsenceDetail?.name || 'Employee',
    });
  }
}
